import {
    faArrowUpRightDots,
    faCheck,
    faClockThree,
    faLanguage,
    faPlus,
    faPuzzlePiece,
    faTags,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@sharedComponents/Button';
import Card from '@sharedComponents/Card';
import FlexBox from '@sharedComponents/FlexBox';
import Tag from '@sharedComponents/Tag';
import Tooltip from '@sharedComponents/Tooltip';
import Typography from '@sharedComponents/Typography';
import { getTranslatedString } from '@src/i18n';
import { effectiveAverageDuration } from '@utils/duration';
import { LanguageNames } from '@utils/languageNames';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as style from './style.module.css';

// take a collection in parameter
const BookingTestCard = ({
    test,
    tag = '',
    action,
    onView,
    groupTest = false,
    selectedTests = [],
    selectionTab = false,
    isDetailsDisabled = false,
    ...props
}) => {
    const { t } = useTranslation();

    const [hoverSelected, setHoverSelected] = useState(false);
    const [isTitleTruncated, setIsTitleTruncated] = useState(false);
    const titleRef = useRef(null);

    // Test structure variables
    const tests = test.tests || [];
    const nbLanguages = new Set(tests.map((t) => t.language)).size;
    const uniqueLevelsCount = new Set(tests.map((t) => t.level)).size;

    // Selection state variables
    const isOneTestSelected = tests.some((level) => level.isSelected);
    const selectedTest = tests.find((level) => level.isSelected);

    // Test type checking variables
    const hasCategoryTests = tests.some((t) => t.categoryId === 9011 || [1, 23].includes(t.testNature));
    const hasCategoryNineTests = tests.some((t) => t.categoryId === 9011);
    const hasTestNature23 = tests.some((t) => t.testNature === 23);
    const hasTestNature1 = tests.some((t) => t.testNature === 1);

    // Duration variables
    const hasTestsWithDuration = tests.length > 0 && tests.some((level) => effectiveAverageDuration(level) > 0);
    const averageDurationMinutes = hasTestsWithDuration
        ? Math.floor(tests.reduce((acc, level) => acc + effectiveAverageDuration(level), 0) / tests.length / 60)
        : 0;

    // Tags variables
    const domainTag = test.tags?.find((tag) => tag.type === 'domain');

    // Content variables
    const name = getTranslatedString(test.name);
    const type = name.split('-')[0].trim();
    const singleLevelTitle = uniqueLevelsCount === 1 ? getTranslatedString(tests[0].title) : null;
    const domainText = domainTag ? getTranslatedString(domainTag.name) : null;

    useEffect(() => {
        if (titleRef.current) {
            setIsTitleTruncated(titleRef.current.scrollHeight > titleRef.current.clientHeight + 1);
        }
    }, [name]);

    // Selected languages, take selectedTest (if in the allTest tab) otherwhise take from tests (in the YourSelectionTab)
    const selectedLanguages =
        selectedTests?.length > 0
            ? selectedTests?.reduce((acc, test) => {
                  if (test.isSelected && test.language && !acc.includes(test.language)) {
                      acc.push(test.language);
                  }
                  return acc;
              }, [])
            : tests?.reduce((acc, test) => {
                  if (test.isSelected && test.language && !acc.includes(test.language)) {
                      acc.push(test.language);
                  }
                  return acc;
              }, []);

    // Selected levels titles, take selectedTest (if in the allTest tab) otherwhise take from tests (in the YourSelectionTab)
    const selectedLevels =
        selectedTests?.length > 0
            ? selectedTests?.reduce((acc, test) => {
                  const levelTitle = getTranslatedString(test.title);
                  if (test.isSelected && !acc.includes(levelTitle)) {
                      acc.push(levelTitle);
                  }
                  return acc;
              }, [])
            : tests?.reduce((acc, test) => {
                  const levelTitle = getTranslatedString(test.title);
                  if (test.isSelected && !acc.includes(levelTitle)) {
                      acc.push(levelTitle);
                  }
                  return acc;
              }, []) || [];

    // Recover language mapping with translation
    const languageNames = LanguageNames(t);
    const selectedLanguageNames = selectedLanguages.map((lang) => languageNames[lang] || lang);

    const selectionTabSubtestId = tests.length > 1 && isOneTestSelected && selectedTest?.id;

    const levelsText =
        selectedLevels.length > 0
            ? selectedLevels.join(', ')
            : singleLevelTitle ||
              (!isOneTestSelected && uniqueLevelsCount > 1 ? t('{{count}} levels', { count: uniqueLevelsCount }) : null);

    const categoryText = hasCategoryTests
        ? [hasCategoryNineTests && type, hasTestNature23 && type, hasTestNature1 && t('Hands-on simulation')].filter(Boolean).join('')
        : null;

    const languagesText =
        nbLanguages > 1
            ? selectedLevels.length > 0 && selectedLanguageNames.length > 0
                ? selectedLanguageNames.join(', ')
                : `${nbLanguages} ${t('languages')}`
            : null;

    const durationText = hasTestsWithDuration ? t('{{count}} min on average', { count: averageDurationMinutes }) : null;

    const contentLines = [
        { key: 'levels', icon: faArrowUpRightDots, text: levelsText },
        { key: 'domain', icon: faTags, text: domainText, title: domainText },
        { key: 'duration', icon: faClockThree, text: durationText },
        { key: 'languages', icon: faLanguage, text: languagesText },
        { key: 'category', icon: faPuzzlePiece, text: categoryText },
    ]
        .filter(({ text }) => Boolean(text))
        .slice(0, 3);

    return (
        <Card theme="booking" {...props}>
            {tag && (
                <div className={style.tag}>
                    <Tag color={tag[0]?.isSpecific ? 'secondary' : 'acid'} textColor={tag[0]?.isSpecific ? 'black' : 'white'} docked>
                        {getTranslatedString(tag)}
                    </Tag>
                </div>
            )}
            <div className={cx(style.card, { [style.tagged]: tag })}>
                <div className={style.header}>
                    <Tooltip text={name} popupIcon={false} display={isTitleTruncated}>
                        <Typography ref={titleRef} color="neutral-primary" fontWeight="semi-bold" className={style.headerText}>
                            {name}
                        </Typography>
                    </Tooltip>
                </div>
                <div className={style.content}>
                    {contentLines.map(({ key, icon, text, title }) => (
                        <div key={key} className={style.contentLine}>
                            <FontAwesomeIcon icon={icon} className={style.contentIcon} />
                            <div className={style.contentText} title={title}>
                                {text}
                            </div>
                        </div>
                    ))}
                </div>
                <div className={style.actions}>
                    <Button
                        disabled={isDetailsDisabled}
                        ghost
                        label={t('View details')}
                        action={() => onView(selectionTabSubtestId)}
                        noPadding
                    />
                    <FlexBox gap="xs" justifyContent="flex-end">
                        {/* If test is open from a testGroup conditionnal display of the button */}
                        {!selectionTab && !selectedTests.length && !groupTest && (
                            <Button icon={faPlus} rounded theme="light" action={action} />
                        )}
                        {(props.isSelected || props.partiallySelected) && (
                            <div onMouseEnter={() => setHoverSelected(true)} onMouseLeave={() => setHoverSelected(false)}>
                                <Button
                                    rounded
                                    action={() => action('remove', selectionTabSubtestId)}
                                    icon={hoverSelected ? faTrash : faCheck}
                                    style={{
                                        backgroundColor: hoverSelected
                                            ? 'var(--background-color-error-strong-primary)'
                                            : 'var(--background-color-branded-strong-primary)',
                                    }}
                                />
                            </div>
                        )}
                    </FlexBox>
                </div>
            </div>
        </Card>
    );
};

export default BookingTestCard;
